import React, { useEffect, useState } from 'react';
import { Alignment, Fit, Layout, useRive } from '@rive-app/react-canvas';
import { debug } from '@/utils/logger';

/** Rive asset played while a social link (Instagram/TikTok) is being parsed for places. */
export const SOCIAL_PARSE_RIVE_ASSET_URL = '/gt_ig_parse.riv';

// Shared across mounts so remounting the animation (e.g. each "Find places" tap)
// reuses the already-downloaded asset instead of fetching it again.
const assetBufferCache = new Map<string, Promise<ArrayBuffer>>();

/** Starts downloading a Rive asset ahead of time so it is ready when the animation mounts. */
export function preloadRiveAsset(url: string): Promise<ArrayBuffer> {
    let pending = assetBufferCache.get(url);
    if (!pending) {
        pending = fetch(url).then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.arrayBuffer();
        });
        pending.catch(() => assetBufferCache.delete(url));
        assetBufferCache.set(url, pending);
    }
    return pending;
}

// Capacitor iOS serves the webview on the `capacitor://` custom URL scheme,
// which prevents Rive's internal fetch() from loading `.riv` files (same issue
// does NOT occur on Android, which uses `https://`). Pre-fetching as an
// ArrayBuffer and passing `buffer` to useRive bypasses the internal fetch.
function useRiveAssetBuffer(url: string): ArrayBuffer | null {
    const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
    useEffect(() => {
        let cancelled = false;
        preloadRiveAsset(url)
            .then((buf) => {
                if (!cancelled) setBuffer(buf);
            })
            .catch((err) => {
                debug('[RiveParseAnimation] Failed to pre-fetch Rive asset', { url, error: err });
            });
        return () => {
            cancelled = true;
        };
    }, [url]);
    return buffer;
}

const DEFAULT_LAYOUT = new Layout({
    fit: Fit.Contain,
    alignment: Alignment.Center,
});

const TOP_ALIGNED_LAYOUT = new Layout({
    fit: Fit.Contain,
    alignment: Alignment.TopCenter,
});

interface RiveParseAnimationProps {
    assetUrl: string;
    className?: string;
    style?: React.CSSProperties;
    layout?: Layout;
    onReady?: () => void;
}

export function RiveParseAnimation({
    assetUrl,
    className,
    style,
    layout = DEFAULT_LAYOUT,
    onReady,
}: RiveParseAnimationProps) {
    const buffer = useRiveAssetBuffer(assetUrl);
    const { RiveComponent } = useRive(
        buffer
            ? {
                buffer,
                autoplay: true,
                layout,
                onLoad: onReady,
            }
            : null,
    );

    // RiveComponent drops `style` when `className` is set, so size a wrapper instead.
    return (
        <div className={className} style={style}>
            <RiveComponent />
        </div>
    );
}

interface SocialParseAnimationProps {
    className?: string;
    /** Text shown if the animation is not ready within `fallbackDelayMs`. */
    fallbackText?: string;
    fallbackDelayMs?: number;
    /**
     * Share of the animation's height to show, from the top (0–1). The canvas is drawn
     * taller than the box and the rest is clipped, e.g. 0.6 hides the bottom 40%.
     */
    visibleFraction?: number;
}

/**
 * The "scanning post for spots" animation from the Import Inspo flow, with a
 * plain-text fallback when the Rive asset cannot load in time.
 */
export function SocialParseAnimation({
    className,
    fallbackText = 'Scanning Reel For Spots...',
    fallbackDelayMs = 1500,
    visibleFraction = 1,
}: SocialParseAnimationProps) {
    const isCropped = visibleFraction > 0 && visibleFraction < 1;
    const [isReady, setIsReady] = useState(false);
    const [showFallback, setShowFallback] = useState(false);

    useEffect(() => {
        if (isReady) {
            setShowFallback(false);
            return;
        }
        const timer = window.setTimeout(() => setShowFallback(true), fallbackDelayMs);
        return () => window.clearTimeout(timer);
    }, [isReady, fallbackDelayMs]);

    return (
        <div className={`relative overflow-hidden ${className ?? ''}`}>
            <RiveParseAnimation
                assetUrl={SOCIAL_PARSE_RIVE_ASSET_URL}
                // Absolute fill: a %-height canvas collapses to 0 inside flex/scroll parents.
                className="absolute inset-x-0 top-0"
                style={{ height: `${100 / (isCropped ? visibleFraction : 1)}%` }}
                layout={isCropped ? TOP_ALIGNED_LAYOUT : DEFAULT_LAYOUT}
                onReady={() => setIsReady(true)}
            />
            {showFallback ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white font-manrope text-base text-[#252528]">
                    {fallbackText}
                </div>
            ) : null}
        </div>
    );
}
